import React from 'react'
import { useInterval } from 'react-use'
import { usePlanStepsContext } from './context'
import { PlanStepTaskItems, Task } from './task-items'
import { useAnimationState } from './use-animation-state'

type PlanStepUpdatesListProps = Readonly<{
  tasks: Task[]
  isInProgress: boolean
  enableAnimation: boolean
  stepId: string
  workflowId: string
}>

export const PlanStepUpdatesList: React.FC<PlanStepUpdatesListProps> = ({
  tasks,
  isInProgress,
  enableAnimation,
  stepId,
}) => {
  const refScroll = React.useRef<HTMLDivElement>(null)

  const {
    isAnimating,
    currentIndex,
    hasAutoExpanded,
    hasAutoCollapsed,
    setIndex: setAnimationIndex,
    markAutoExpanded,
    markAutoCollapsed,
  } = useAnimationState(stepId, enableAnimation, isInProgress, tasks.length)

  // Get context from parent
  const { setExpandedStepIds } = usePlanStepsContext()

  // Animation interval
  useInterval(
    () => {
      if (!isAnimating) {
        return
      }

      // Auto-expand when starting animation
      if (!hasAutoExpanded && currentIndex === 0) {
        markAutoExpanded()
        setExpandedStepIds((prev: Set<string>) => {
          const next = new Set(prev)
          next.add(stepId)
          return next
        })
      }

      // Auto-collapse when animation completes
      if (!hasAutoCollapsed && currentIndex === tasks.length - 1) {
        markAutoCollapsed()
        setExpandedStepIds((prev: Set<string>) => {
          const next = new Set(prev)
          next.delete(stepId)
          return next
        })
        return
      }

      // Progress animation
      if (currentIndex < tasks.length - 1) {
        const nextIndex = currentIndex + 1
        setAnimationIndex(nextIndex)

        // Scroll to bottom after animation
        setTimeout(() => {
          if (refScroll.current) {
            refScroll.current.scrollTo({
              top: refScroll.current.scrollHeight,
              behavior: 'smooth',
            })
          }
        }, 250)
      }
    },
    isAnimating && currentIndex < tasks.length - 1 ? 250 : null,
  )

  // Sync external index with animation state - use ref to avoid infinite loop
  const prevCurrentIndexRef = React.useRef(currentIndex)
  React.useEffect(() => {
    if (prevCurrentIndexRef.current !== currentIndex) {
      prevCurrentIndexRef.current = currentIndex
    }
  }, [currentIndex])

  React.useEffect(() => {
    if (!isAnimating && currentIndex < tasks.length - 1) {
      setAnimationIndex(tasks.length - 1)
    }
  }, [isAnimating, tasks.length, setAnimationIndex, currentIndex])

  React.useEffect(() => {
    setTimeout(() => {
      if (refScroll.current) {
        refScroll.current.scrollTo({ top: refScroll.current.scrollHeight, behavior: 'smooth' })
      }
    }, 250)
  }, [tasks.length])

  return (
    <div className='rounded-lg bg-neutral-75 bg-opacity-85 p-4'>
      <div
        ref={refScroll}
        className='flex max-h-40 snap-y flex-col overflow-y-auto transition-all first:snap-none'
      >
        <PlanStepTaskItems tasks={tasks} animateIndex={currentIndex} isCompact />
      </div>
    </div>
  )
}
